/**
 * apiFetch — drop-in `fetch` wrapper for the React app.
 *
 * ceo_instruction_ref:
 *   p0_1_apifetch_wrapper_stage_17may2026
 *
 * Behaviour
 * ─────────
 * • Auto-attaches `Authorization: Bearer <token>` from
 *   localStorage('rk_token') when no Authorization header
 *   is supplied by the caller.
 * • Auto-attaches `Content-Type: application/json` for
 *   string/object bodies when not supplied.
 * • On HTTP 401, clears local storage tokens, signals
 *   `AuthContext.logout()`, and redirects the browser to
 *   `/login?reason=session_expired&from=<current_path>`.
 *   The user lands on a friendly login page instead of
 *   seeing the raw `{"detail": "Not authenticated"}`.
 * • Returns the original `Response` object — call sites
 *   keep their existing `await r.json()` / `r.ok` logic.
 *
 * Usage
 * ─────
 *   import apiFetch from '@/utils/apiFetch';
 *
 *   const r = await apiFetch(`${API}/api/orders`);
 *   if (!r.ok) { ... }
 *   const data = await r.json();
 *
 * AuthContext integration
 * ───────────────────────
 * `AuthProvider` calls `registerLogoutHandler(logout)` on
 * mount. The wrapper invokes it on 401 so React state is
 * cleared cleanly alongside the localStorage purge.
 */

let _logoutHandler = null;
let _redirecting = false;

export function registerLogoutHandler(fn) {
  _logoutHandler = typeof fn === 'function' ? fn : null;
}

function _clearTokens() {
  try {
    localStorage.removeItem('rk_token');
    localStorage.removeItem('rk_refresh_token');
    localStorage.removeItem('rk_impersonation');
  } catch (_) {
    // localStorage may be unavailable (SSR / sandboxed
    // iframes); fail silently.
  }
}

function _redirectToLogin(reason) {
  if (_redirecting) return;
  if (typeof window === 'undefined') return;
  const here = window.location.pathname + window.location.search;
  // Don't loop the login page on itself.
  if (window.location.pathname.startsWith('/login')) return;
  _redirecting = true;
  const params = new URLSearchParams();
  params.set('reason', reason || 'session_expired');
  if (here && here !== '/') params.set('from', here);
  window.location.assign(`/login?${params.toString()}`);
}

export default async function apiFetch(input, init = {}) {
  const opts = { ...init };
  // stock_check_draft_autosave_18sep2026: callers that must handle
  // a 401 themselves (e.g. the rep count page, so the redirect can
  // never outrun the error and discard typed work) pass
  // { skipAuthRedirect: true } and receive the raw 401 response.
  const skipAuthRedirect = !!opts.skipAuthRedirect;
  delete opts.skipAuthRedirect;
  const headers = new Headers(opts.headers || {});

  // Auto-attach Authorization.
  if (!headers.has('Authorization')) {
    try {
      const t = localStorage.getItem('rk_token');
      if (t) headers.set('Authorization', `Bearer ${t}`);
    } catch (_) { /* localStorage unavailable */ }
  }

  // Auto-attach Content-Type for JSON bodies.
  if (
    opts.body &&
    !(opts.body instanceof FormData) &&
    !(opts.body instanceof Blob) &&
    !(opts.body instanceof URLSearchParams) &&
    !headers.has('Content-Type')
  ) {
    headers.set('Content-Type', 'application/json');
  }
  opts.headers = headers;

  let response;
  try {
    response = await fetch(input, opts);
  } catch (err) {
    // Network / CORS errors propagate to the caller — they
    // are not auth errors.
    throw err;
  }

  if (response.status === 401 && !skipAuthRedirect) {
    _clearTokens();
    try { if (_logoutHandler) _logoutHandler(); } catch (_) { /* noop */ }
    _redirectToLogin('session_expired');
  }
  return response;
}

/**
 * Attach an axios response interceptor that mirrors the
 * fetch wrapper's 401 behaviour. Returns a detach
 * function. Idempotent — duplicate attaches are no-ops.
 */
let _axiosInterceptorId = null;
export function attachAxiosInterceptor(axiosInstance) {
  if (!axiosInstance || typeof axiosInstance.interceptors === 'undefined') {
    return () => {};
  }
  if (_axiosInterceptorId !== null) {
    return () => {
      try { axiosInstance.interceptors.response.eject(_axiosInterceptorId); } catch (_) { /* noop */ }
      _axiosInterceptorId = null;
    };
  }
  _axiosInterceptorId = axiosInstance.interceptors.response.use(
    (resp) => resp,
    (err) => {
      const status = err && err.response && err.response.status;
      if (status === 401) {
        _clearTokens();
        try { if (_logoutHandler) _logoutHandler(); } catch (_) { /* noop */ }
        _redirectToLogin('session_expired');
      }
      return Promise.reject(err);
    },
  );
  return () => {
    try { axiosInstance.interceptors.response.eject(_axiosInterceptorId); } catch (_) { /* noop */ }
    _axiosInterceptorId = null;
  };
}
